import React from 'react'
import { useSignal } from '@preact/signals-react'
import { Box, Button, Card, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
import { IconSeeker } from '@ui-seeker/core'

import { AppCardTitle, AppRetailerBlock, secondaryButtonSx } from '@components'
import { dsColorNew } from '@palette'
import { displayDay } from '@utils'
import { realTimeTasks } from 'Pages/RealTimeHistory/RealTime/@signals'


// The three kinds of searches without prices the API reports, in the order they are listed
const KINDS = [
	{ key: 'noAvailData', label: 'Not available', icon: 'triangle-exclamation', bg: dsColorNew.noteAmberBg, fg: dsColorNew.noteAmber },
	{ key: 'errorData', label: 'Error data', icon: 'circle-exclamation', bg: dsColorNew.dangerBg, fg: dsColorNew.dangerRed },
	{ key: 'badConfigData', label: 'Bad config', icon: 'rotate-exclamation', bg: dsColorNew.pillTrackBg, fg: dsColorNew.textMuted },
]

const chipSx = (kind) => ({
	display: 'inline-flex', alignItems: 'center', gap: 0.75, height: 26, px: 1.25, borderRadius: 999,
	fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', backgroundColor: kind.bg, color: kind.fg,
})

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`

/**
 * Searches of a Real Time run that came back without prices. One summary
 * line (how many failed, of which kind, how many sites and dates); "Show
 * detail" opens one row per site and pickup date with each rental length,
 * the ones with a screenshot open it, as the per-site popover did before.
 */
export default function RealTimeTableErrorsData({ tableDataErrors }) {
	const open = useSignal(false)

	const rows = KINDS.flatMap(kind => Object.entries(tableDataErrors[kind.key] || {}).flatMap(([date, sites]) =>
		(sites || []).map(site => ({ ...site, date, kind }))))
		.sort((a, b) => a.date.localeCompare(b.date) || KINDS.indexOf(a.kind) - KINDS.indexOf(b.kind) || String(a.siteName).localeCompare(String(b.siteName)))

	const counts = KINDS.map(kind => ({ kind, n: rows.filter(r => r.kind === kind).reduce((s, r) => s + (r.info?.length || 0), 0) })).filter(c => c.n)
	const failed = counts.reduce((s, c) => s + c.n, 0)
	const total = realTimeTasks.value?.completeTasks
	const sites = new Set(rows.map(r => r.siteName)).size
	const dates = new Set(rows.map(r => r.date)).size

	return <Card data-tour-id="tour-realtime-errors" sx={{ mb: 2, px: 2.25, py: 1.75 }}>
		<Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
			<AppCardTitle
				icon="triangle-exclamation" iconBg={dsColorNew.dangerBg} iconColor={dsColorNew.dangerRed}
				title={`${total ? `${failed} of ${total}` : failed} search${(total || failed) === 1 ? '' : 'es'} returned no prices`}
				subtitle={`${plural(sites, 'site')} · ${plural(dates, 'pickup date')}`}
			/>
			<Box sx={{ flex: 1 }}/>
			{counts.map(({ kind, n }) => <Box key={kind.key} component="span" sx={chipSx(kind)}>
				<IconSeeker icon={kind.icon} sx={{ fontSize: 12 }}/>{kind.label} · {n}
			</Box>)}
			<Button
				onClick={() => open.value = !open.value}
				endIcon={<IconSeeker icon={open.value ? 'chevron-up' : 'chevron-down'} sx={{ fontSize: '11px !important' }}/>}
				sx={{ ...secondaryButtonSx, height: 34 }}
			>
				{open.value ? 'Hide detail' : 'Show detail'}
			</Button>
		</Box>

		{open.value && <Table size="small" sx={{ mt: 1.5, '& td': { fontSize: 13, py: 1 } }}>
			<TableHead>
				<TableRow>
					<TableCell sx={{ width: 260 }}>Site</TableCell>
					<TableCell sx={{ width: 170 }}>Problem</TableCell>
					<TableCell sx={{ width: 140 }}>Pickup date</TableCell>
					<TableCell>Rental lengths</TableCell>
				</TableRow>
			</TableHead>
			<TableBody>
				{rows.map(r => <TableRow key={`${r.kind.key}-${r.date}-${r.siteName}`}>
					<TableCell><AppRetailerBlock retailer={r.siteName} country={r.siteCountry} icon={r.siteIco}/></TableCell>
					<TableCell><Box component="span" sx={chipSx(r.kind)}>{r.kind.label}</Box></TableCell>
					<TableCell sx={{ whiteSpace: 'nowrap' }}>{displayDay(r.date)}</TableCell>
					<TableCell>
						<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
							{(r.info || []).map(({ lor, screenshot }) => <LorChip key={`${lor}-${screenshot}`} lor={lor} screenshot={screenshot}/>)}
						</Box>
					</TableCell>
				</TableRow>)}
			</TableBody>
		</Table>}
	</Card>
}

function LorChip({ lor, screenshot }) {
	const label = `${lor} day${Number(lor) === 1 ? '' : 's'}`
	return <Box
		component="span"
		title={screenshot ? 'Open the screenshot' : undefined}
		onClick={() => screenshot && window.open(screenshot)}
		sx={{
			display: 'inline-flex', alignItems: 'center', gap: 0.5, height: 24, px: 1, borderRadius: '6px',
			fontSize: 12, fontWeight: 500, border: `1px solid ${dsColorNew.grayBorder}`,
			backgroundColor: dsColorNew.white, color: dsColorNew.textTitle,
			...screenshot && {
				cursor: 'pointer', borderColor: dsColorNew.noteInfoBorder, backgroundColor: dsColorNew.noteInfoBg, color: dsColorNew.noteInfo,
				'&:hover': { borderColor: dsColorNew.appBlue },
			},
		}}
	>
		{label}
		{!!screenshot && <IconSeeker icon="image" sx={{ fontSize: 11 }}/>}
	</Box>
}
